import { useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useRef } from 'react';
import {
  useOnboardingChecklist,
  useRecordExplicitCompletion,
  shouldShowChecklist,
} from '../../../hooks/data/useOnboardingChecklist';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { safeAnalytics } from '../../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';

interface UseChecklistPanelOptions {
  isBirdOpen: boolean;
  onToggleBird?: () => void;
  /** Open the invite dialog — the checklist's "invite a teammate" CTA. */
  onOpenInvite: () => void;
}

interface UseChecklistPanelResult {
  showChecklist: boolean;
  checklistData: ReturnType<typeof useOnboardingChecklist>['data'];
  isPanelOpen: boolean;
  handleOpenPanel: () => void;
  closePanel: () => void;
}

/**
 * Onboarding-checklist panel state for the nav rail: eligibility, the two
 * auto-open moments, and the invite CTA's event. Lifted out of the old Sidebar
 * unchanged — the effects are order- and ref-sensitive, so this is a move, not
 * a rewrite.
 *
 * The panel's own render is wired at layout-integration time (it needs the
 * rail width and a feedback opener, neither of which the rail owns) — this
 * hook only owns whether it should be open.
 */
export function useChecklistPanel({
  isBirdOpen,
  onToggleBird,
  onOpenInvite,
}: UseChecklistPanelOptions): UseChecklistPanelResult {
  const { isOpen: isPanelOpen, onOpen: openPanel, onClose: closePanel } = useDisclosure();

  const {
    data: checklistData,
    isLoading: checklistLoading,
    isError: checklistError,
  } = useOnboardingChecklist();

  const prevAllComplete = useRef(false);
  const hasAutoOpened = useRef(false);

  // Auto-open panel on first load for users who haven't interacted with the checklist yet
  useEffect(() => {
    if (hasAutoOpened.current) return;
    if (checklistLoading || checklistError) return;
    // A unified setup list answers `state: null`, so it is never eligible: it
    // lives on the dashboard and the rail never opens itself over it.
    const eligible = shouldShowChecklist(checklistData?.state ?? null);
    if (!eligible || !checklistData) return;
    // Don't auto-open if user has already made progress — they know it exists
    if (checklistData.completedCount > 1) return;
    hasAutoOpened.current = true;
    openPanel();
    // Close the Bird chat panel so the onboarding checklist isn't sitting on
    // top of an open chat — both compete for the same left-side real estate.
    if (isBirdOpen && onToggleBird) {
      onToggleBird();
    }
    safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_SHOWN);
  }, [checklistLoading, checklistError, checklistData, openPanel, isBirdOpen, onToggleBird]);

  // Auto-open panel when all steps complete + track analytics. The unified
  // list belongs to one workspace and has no auto-dismiss, so it reports only
  // a completion this session watched happen in THIS workspace; otherwise
  // every load of a finished list would report it, and a switch would carry
  // one workspace's state into the next.
  const currentWorkspaceId = useWorkspaceId();
  const unifiedIncompleteIn = useRef<string | null>(null);
  const unifiedReportedIn = useRef<string | null>(null);
  useEffect(() => {
    const unified = checklistData?.unified;
    if (unified) {
      const workspaceId = currentWorkspaceId;
      // Right after a switch, the cache can still hold the previous
      // workspace's list for a render: it says nothing about this one.
      if (checklistData.forWorkspaceId !== workspaceId) return;
      if (!checklistData.allCoreStepsComplete) {
        unifiedIncompleteIn.current = workspaceId;
        return;
      }
      if (unifiedIncompleteIn.current !== workspaceId) return;
      // A view filtered to no steps (a guest whose last visible item went) is
      // complete only in the sense that nothing is left to do: not a finish.
      if (checklistData.totalCoreSteps === 0) return;
      if (unifiedReportedIn.current === workspaceId) return;
      unifiedReportedIn.current = workspaceId;
      safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_ALL_COMPLETE, {
        variant: unified.variant,
      });
      return;
    }
    if (checklistData?.allCoreStepsComplete && !prevAllComplete.current) {
      prevAllComplete.current = true;
      openPanel();
      safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_ALL_COMPLETE);
    }
  }, [checklistData, openPanel, currentWorkspaceId]);

  // A unified list's Ask step ticks when a message is actually sent. Here, not
  // in the panel: the rail always mounts this hook, while a closed mobile
  // drawer mounts none of its content.
  const recordCompletion = useRecordExplicitCompletion().mutate;
  // The panel only mounted for a list with Ask on it; here that is explicit
  // (a read-only seat's list has no Ask step, and chat is open to every role).
  const askPending =
    !!checklistData?.unified &&
    checklistData.unified.coreSteps.includes('ask_bird') &&
    checklistData.forWorkspaceId === currentWorkspaceId &&
    !checklistData.unified.dismissedAt &&
    !checklistData.completions.ask_bird;
  useEffect(() => {
    if (!askPending) return;
    const handleMessageSent = () => recordCompletion('ask_bird_clicked');
    window.addEventListener('onboarding:birdMessageSent', handleMessageSent);
    return () => window.removeEventListener('onboarding:birdMessageSent', handleMessageSent);
  }, [askPending, recordCompletion]);

  // Fire analytics when panel opens
  const handleOpenPanel = useCallback(() => {
    openPanel();
    safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_SHOWN);
  }, [openPanel]);

  // The invite CTA's event (the checklist and the dashboard onboarding fire
  // it): the rail owns the invite dialog. The create CTAs call the Create
  // modal's store directly.
  useEffect(() => {
    const handleOpenInvite = () => onOpenInvite();
    window.addEventListener('onboarding:openInvite', handleOpenInvite);
    return () => window.removeEventListener('onboarding:openInvite', handleOpenInvite);
  }, [onOpenInvite]);

  const showChecklist =
    !checklistLoading &&
    !checklistError &&
    (checklistData?.unified
      ? checklistData.forWorkspaceId === currentWorkspaceId &&
        !checklistData.unified.dismissedAt &&
        !checklistData.allCoreStepsComplete
      : shouldShowChecklist(checklistData?.state ?? null));

  return { showChecklist, checklistData, isPanelOpen, handleOpenPanel, closePanel };
}
